import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { Observable } from '@apollo/client'
import { type ZenObservable } from 'zen-observable'
import { ApolloError, type ServerError, type ServerParseError } from '@apollo/client'
import instacart from '../common/instacart'
import { errors } from '../../utils/error-handling/errors'
import clientEnv from '../../utils/global/clientEnv'
import {
  isDisabledAccountError,
  isUndeterminedAccountError,
  isTokenExpiredError,
} from '../../api/utils/apollo/errors/isError.hooks'
import { logout, LogoutReason } from '../../gin-and-tonic/containers/log-out/utils/logout'
import { isOnLoginPage } from '../../utils/auth/isLoginPage'
import { type EnterpriseGraphQLError } from './errors'
import { TokenRefreshService, TokenRefreshError } from '../../api/utils/auth/tokenRefresh'
import { getRefreshToken, removeRefreshToken } from '../../api/utils/auth/authStorage'
import {
  logAuthenticationError,
  extractUserContextFromJWT,
  ERROR_TYPES,
  AUTH_STAGES,
  FAILURE_REASONS,
  OAUTH_ERROR_CODES,
} from '../../utils/auth'
import {
  getSessionFromStorage,
  setSessionInStorage,
} from '../../api/utils/apollo/session/sessionToken.utils'
import { isExpectedError } from '../../utils/expected-errors'

const fetchTokenWithRefresh = async (): Promise<string | null> => {
  const session = getSessionFromStorage()
  return session?.token || null
}

/**
 * Logs token refresh errors with structured context and clears refresh token if invalid
 * Exported for testing purposes
 */
export const handleTokenRefreshError = async (
  refreshError: unknown,
  operation: { operationName: string },
  triggerType: 'network_error' | 'graphql_error'
): Promise<void> => {
  // Extract user context for logging
  const session = getSessionFromStorage()
  const userContext = session?.token
    ? extractUserContextFromJWT(session.token)
    : { email: null, emailError: 'no_session', userId: 'unknown' }

  // Handle structured TokenRefreshError
  if (refreshError instanceof TokenRefreshError) {
    const { statusCode, errorCode, responseText } = refreshError

    // Determine failure reason based on error details
    const failureReason =
      errorCode === OAUTH_ERROR_CODES.INVALID_GRANT ||
      responseText?.toLowerCase().includes(OAUTH_ERROR_CODES.INVALID_GRANT)
        ? FAILURE_REASONS.REFRESH_TOKEN_INVALID
        : FAILURE_REASONS.HTTP_ERROR

    // Log the refresh failure with structured information
    logAuthenticationError(
      refreshError,
      ERROR_TYPES.TOKEN_REFRESH_FAILURE,
      AUTH_STAGES.TOKEN_REFRESH,
      failureReason,
      userContext,
      {
        statusCode,
        errorCode,
        responseText: responseText?.substring(0, 200), // Limit length for logging
        operationName: operation.operationName,
        refreshAttempt: true,
        triggerType,
      }
    )

    // If refresh token is invalid, clear it so we don't keep trying
    if (failureReason === FAILURE_REASONS.REFRESH_TOKEN_INVALID) {
      await removeRefreshToken()
    }
  } else {
    // Handle unexpected errors
    logAuthenticationError(
      refreshError as Error,
      ERROR_TYPES.TOKEN_REFRESH_FAILURE,
      AUTH_STAGES.TOKEN_REFRESH,
      FAILURE_REASONS.NETWORK_ERROR,
      userContext,
      {
        operationName: operation.operationName,
        refreshAttempt: true,
        triggerType,
        errorMessage: (refreshError as Error).message,
      }
    )
  }
}

export const authLink = setContext(async (_, { headers }) =>
  fetchTokenWithRefresh()
    .then(token => ({
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        ...(clientEnv.PUBLIC_CLIENT_CYPRESS_TOKEN && {
          'x-cypress-token': clientEnv.PUBLIC_CLIENT_CYPRESS_TOKEN,
        }),
      },
    }))
    .catch(() => {
      return { headers }
    })
)

export const errorLink = onError(error => {
  const { networkError, operation, forward } = error

  if (networkError) {
    if ((networkError as ServerError | ServerParseError).statusCode === 401) {
      // Return an Observable to handle async token refresh
      return new Observable(observer => {
        // Declare subscriber in outer scope so cleanup function can access it via closure
        let subscriber: ZenObservable.Subscription | undefined
        ;(async () => {
          try {
            // Check if refresh token is available
            const refreshToken = await getRefreshToken()

            if (!refreshToken) {
              instacart.logoutAndRedirect()
              observer.complete()
              return
            }

            const newToken = await TokenRefreshService.refreshAccessToken()

            // Update stored session (ID token) with new one
            setSessionInStorage({ token: newToken })

            // Update operation context and retry
            operation.setContext(({ headers = {} }) => ({
              headers: {
                ...headers,
                authorization: `Bearer ${newToken}`,
              },
            }))

            // Subscribe to the retry and forward results to our observer
            subscriber = forward(operation).subscribe({
              next: result => {
                observer.next(result)
              },
              error: err => {
                observer.error(err)
              },
              complete: () => {
                observer.complete()
              },
            })
          } catch (refreshError) {
            await handleTokenRefreshError(refreshError, operation, 'network_error')
            instacart.logoutAndRedirect()
            observer.complete()
          }
        })()

        // Return cleanup function that unsubscribes from the forwarded operation
        return () => {
          if (subscriber) {
            subscriber.unsubscribe()
          }
        }
      })
    } else {
      // AbortErrors are intentional request cancellations when React cancels in-flight
      // GraphQL queries during component unmount, navigation, or filter changes.
      // These are expected behavior, not real errors.
      const isAbortError = networkError instanceof DOMException && networkError.name === 'AbortError'
      if (isAbortError) {
        console.info('[Apollo] Request aborted (expected behavior):', networkError.message)
        return
      }

      // "Load failed" (Safari) and "Failed to fetch" (Chrome) errors are often transient
      // (user navigates away, network blip, request aborted, ad blockers)
      // Capture as warning instead of error since they're usually not actionable
      const isTransientNetworkError =
        networkError instanceof TypeError &&
        (networkError.message === 'Load failed' || networkError.message === 'Failed to fetch')
      if (isTransientNetworkError) {
        console.warn('[Apollo] Transient network error:', networkError)
      } else {
        errors.captureException(networkError, `Network error with error + ${networkError}`)
      }
    }
  }

  if (error.graphQLErrors) {
    const graphQLErrors = error.graphQLErrors as EnterpriseGraphQLError[]

    const apolloError = new ApolloError({ graphQLErrors })

    if (isDisabledAccountError(apolloError)) {
      // Extract isInternal flag from error extensions
      const [firstError] = apolloError.graphQLErrors
      const isInternal = Boolean(firstError?.extensions?.isInternal)

      logout(LogoutReason.DisabledAccount, isInternal)
      return Observable.of()
    }

    if (isUndeterminedAccountError(apolloError) || isTokenExpiredError(apolloError)) {
      // Return an Observable to handle async token refresh for GraphQL errors too
      return new Observable(observer => {
        // Declare subscriber in outer scope so cleanup function can access it via closure
        let subscriber: ZenObservable.Subscription | undefined
        ;(async () => {
          try {
            // Check if refresh token is available
            const refreshToken = await getRefreshToken()

            if (!refreshToken) {
              // Don't trigger logout during OAuth callback - the session is still being set up
              if (isOnLoginPage()) {
                // During login/callback, just let the query fail gracefully
                observer.error(new Error('Session not ready - login in progress'))
                return
              }

              // User is on a regular page and token expired without refresh capability
              logout(LogoutReason.TokenExpired)
              observer.error(new Error('Token expired and no refresh token available'))
              return
            }

            const newToken = await TokenRefreshService.refreshAccessToken()

            // Update stored session (ID token) with new one
            setSessionInStorage({ token: newToken })

            // Update operation context and retry
            operation.setContext(({ headers = {} }) => ({
              headers: {
                ...headers,
                authorization: `Bearer ${newToken}`,
              },
            }))

            // Subscribe to the retry and forward results to our observer
            subscriber = forward(operation).subscribe({
              next: result => {
                observer.next(result)
              },
              error: err => {
                observer.error(err)
              },
              complete: () => {
                observer.complete()
              },
            })
          } catch (refreshError) {
            await handleTokenRefreshError(refreshError, operation, 'graphql_error')
            logout(LogoutReason.TokenExpired)
            observer.complete()
          }
        })()

        // Return cleanup function that unsubscribes from the forwarded operation
        return () => {
          if (subscriber) {
            subscriber.unsubscribe()
          }
        }
      })
    }

    const { operationName, variables } = error.operation
    const context = error.operation.getContext()

    // Exclude variables from logging for operations that may contain sensitive data, keys, PII, etc.
    // Set `context: { containsSensitiveData: true }` when calling mutations with secrets.
    const containsSensitiveData = context.containsSensitiveData === true
    const safeVariables = containsSensitiveData ? null : variables

    graphQLErrors.forEach(graphQLError => {
      const { message, extensions } = graphQLError

      // Sanitize the error object to remove sensitive data from extensions (e.g., input field)
      const safeError = containsSensitiveData
        ? { ...graphQLError, extensions: { ...extensions, input: undefined as undefined } }
        : graphQLError
      const meta = { variables: safeVariables, error: safeError, operationName }

      if (extensions && extensions.code) {
        const errorMessage = `GraphQL Error ${extensions.code} performing ${operationName}: ${message}`
        const error = new Error(errorMessage) as Error & { dd_fingerprint: string }

        // Custom fingerprint to group errors by operation name and error code instead of default which is to put all GQL errors in the same bucket
        error.dd_fingerprint = `GRAPHQL_ERROR_${extensions.code}_${operationName}`

        if (isExpectedError(extensions.code as string, operationName)) {
          errors.captureWarning(error, errorMessage, {
            extra: meta,
          })
        } else {
          errors.captureException(error, errorMessage, {
            extra: meta,
          })
        }
      } else {
        errors.captureMessage(`Unknown GraphQL Error performing ${operationName}: ${message}`, {
          level: 'warning',
          extra: meta,
        })
      }
    })
  }
})
